import Calendar from "react-calendar";
import { Calendar as CalendarIcon } from "lucide-react";
import { useTheme } from "src/components/layouts/contexts/ThemeContext";
import moment from "moment-timezone";
import { useState, useRef, useEffect } from "react";
import { useNonWorkingDays } from "src/hooks/useNonWorkingDays";
import "react-calendar/dist/Calendar.css";
import "./DateRangePicker.styles";

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  page?: string;
}

const CustomInput = ({
  onClick,
  value,
  isDark,
  label,
}: {
  onClick?: () => void;
  value: string;
  isDark: boolean;
  label: string;
}) => {
  return (
    <div className="relative md:w-36">
      <label className="block text-xs font-normal text-gray-600 dark:text-gray-400 mb-0.5">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          className={`w-full rounded-md border ${
            isDark
              ? "border-[#3d3d3d] bg-transparent text-white focus:border-white focus:ring-white/40"
              : "border-gray-300 bg-white text-gray-900 focus:border-gray-400 focus:ring-gray-400/40"
          } text-sm pl-3 pr-8 py-2 focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-inset cursor-pointer h-[36px]`}
          value={value}
          onClick={onClick}
          readOnly
          placeholder="DD/MM/YYYY"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none">
          <CalendarIcon
            className={`h-4 w-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
          />
        </div>
      </div>
    </div>
  );
};

/** Matches `min-w-[300px]` on the popup below. */
const CALENDAR_WIDTH = 300;
const VIEWPORT_MARGIN = 8;

/**
 * The pickers usually sit at the right edge of a toolbar, where a left-anchored
 * popup runs off screen (and gets clipped by the page's `overflow-hidden`), so
 * the side is chosen from the space actually available when it opens.
 */
const pickAlignment = (anchor: HTMLElement | null): "left" | "right" => {
  if (!anchor || typeof window === "undefined") return "left";
  const rect = anchor.getBoundingClientRect();
  const fitsLeftAnchored =
    rect.left + CALENDAR_WIDTH <= window.innerWidth - VIEWPORT_MARGIN;
  const fitsRightAnchored = rect.right - CALENDAR_WIDTH >= VIEWPORT_MARGIN;
  return fitsLeftAnchored || !fitsRightAnchored ? "left" : "right";
};

const DateRangePicker = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate,
  maxDate,
  page,
}: DateRangePickerProps) => {
  const { isDark } = useTheme();
  const nonWorkingDays = useNonWorkingDays();
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);
  const [startAlign, setStartAlign] = useState<"left" | "right">("left");
  const [endAlign, setEndAlign] = useState<"left" | "right">("left");
  const startCalendarRef = useRef<HTMLDivElement>(null);
  const endCalendarRef = useRef<HTMLDivElement>(null);

  const startValue = moment(startDate).tz("Asia/Kolkata").format("DD/MM/YYYY");
  const endValue = moment(endDate).tz("Asia/Kolkata").format("DD/MM/YYYY");

  const isNonWorkingDay = (date: Date) => {
    return nonWorkingDays.some((nonWorkingDay) =>
      moment(nonWorkingDay).isSame(moment(date), "day"),
    );
  };

  // Handle click outside to close calendars
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        startCalendarRef.current &&
        !startCalendarRef.current.contains(event.target as Node)
      ) {
        setIsStartCalendarOpen(false);
      }
      if (
        endCalendarRef.current &&
        !endCalendarRef.current.contains(event.target as Node)
      ) {
        setIsEndCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStartDateSelect = (date: unknown) => {
    if (!date || Array.isArray(date)) return;
    if (!(date instanceof Date)) return;

    const istDate = moment(date).tz("Asia/Kolkata").toDate();
    onStartDateChange(istDate);
    setIsStartCalendarOpen(false);
  };

  const handleEndDateSelect = (date: unknown) => {
    if (!date || Array.isArray(date)) return;
    if (!(date instanceof Date)) return;

    const istDate = moment(date).tz("Asia/Kolkata").toDate();
    onEndDateChange(istDate);
    setIsEndCalendarOpen(false);
  };

  const tileDisabled = ({ date, view }: { date: Date; view: string }) => {
    if (view === "month") {
      const dateCheck = moment(date).tz("Asia/Kolkata").startOf("day");
      const minCheck = minDate
        ? moment(minDate).tz("Asia/Kolkata").startOf("day")
        : null;
      const maxCheck = maxDate
        ? moment(maxDate).tz("Asia/Kolkata").startOf("day")
        : null;

      if (minCheck && dateCheck.isBefore(minCheck)) return true;
      if (maxCheck && dateCheck.isAfter(maxCheck)) return true;
      if (isNonWorkingDay(date)) return true;

      return false;
    }

    if (view === "year") {
      const monthStart = moment(date).startOf("month");
      const monthEnd = moment(date).endOf("month");
      const minCheck = minDate ? moment(minDate).tz("Asia/Kolkata") : null;
      const maxCheck = maxDate ? moment(maxDate).tz("Asia/Kolkata") : null;

      if (minCheck && monthEnd.isBefore(minCheck)) return true;
      if (maxCheck && monthStart.isAfter(maxCheck)) return true;
      return false;
    }

    if (view === "decade") {
      const yearStart = moment(date).startOf("year");
      const yearEnd = moment(date).endOf("year");
      const minCheck = minDate ? moment(minDate).tz("Asia/Kolkata") : null;
      const maxCheck = maxDate ? moment(maxDate).tz("Asia/Kolkata") : null;

      if (minCheck && yearEnd.isBefore(minCheck)) return true;
      if (maxCheck && yearStart.isAfter(maxCheck)) return true;
      return false;
    }

    return false;
  };

  const tileClassName = ({
    date,
    view,
    isStartCalendar = false,
  }: {
    date: Date;
    view: string;
    isStartCalendar?: boolean;
  }): string => {
    const classes = [];

    if (view === "month") {
      const dateCheck = moment(date).tz("Asia/Kolkata").startOf("day");
      const startCheck = moment(startDate).tz("Asia/Kolkata").startOf("day");
      const endCheck = moment(endDate).tz("Asia/Kolkata").startOf("day");

      // Check if date is the start date
      if (dateCheck.isSame(startCheck, "day")) {
        classes.push("range-start-date");
      }
      // Check if date is the end date
      else if (dateCheck.isSame(endCheck, "day")) {
        classes.push("range-end-date");
      }
      // Check if date is between start and end
      else if (dateCheck.isAfter(startCheck) && dateCheck.isBefore(endCheck)) {
        classes.push("range-between-date");
      }
    }

    if (view === "year" || view === "decade") {
      const selectedMonth = isStartCalendar
        ? moment(startDate).month()
        : moment(endDate).month();
      const selectedYear = isStartCalendar
        ? moment(startDate).year()
        : moment(endDate).year();
      const tileMonth = moment(date).month();
      const tileYear = moment(date).year();

      if (view === "year" && tileMonth === selectedMonth) {
        classes.push("selected-year-month");
      }
      if (view === "decade" && tileYear === selectedYear) {
        classes.push("selected-year-month");
      }
    }

    return classes.join(" ");
  };

  return (
    <div className={`flex gap-2 ${isDark ? "dark" : ""}`}>
      <div className="relative" ref={startCalendarRef}>
        <CustomInput
          value={startValue}
          isDark={isDark}
          label={page === "performance_heatmap" ? "" : "Start Date"}
          onClick={() => {
            setStartAlign(pickAlignment(startCalendarRef.current));
            setIsStartCalendarOpen(!isStartCalendarOpen);
            setIsEndCalendarOpen(false);
          }}
        />

        {isStartCalendarOpen && (
          <div
            className={`absolute top-full z-[9999] mt-1 w-full min-w-[300px] ${
              startAlign === "right" ? "right-0" : "-left-[4px]"
            }`}
          >
            <div
              className={`rounded-lg shadow-lg border ${
                isDark
                  ? "bg-[#1e222d] border-[#3d3d3d] text-white"
                  : "bg-white border-gray-200 text-gray-900"
              }`}
            >
              <div className="p-3">
                <Calendar
                  onChange={handleStartDateSelect}
                  value={moment(startDate).tz("Asia/Kolkata").toDate()}
                  tileDisabled={tileDisabled}
                  tileClassName={(props) =>
                    tileClassName({ ...props, isStartCalendar: true })
                  }
                  minDate={minDate}
                  maxDate={endDate}
                  locale="en-US"
                  calendarType="gregory"
                  showFixedNumberOfWeeks={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="relative" ref={endCalendarRef}>
        <CustomInput
          value={endValue}
          isDark={isDark}
          label={page === "performance_heatmap" ? "" : "End Date"}
          onClick={() => {
            setEndAlign(pickAlignment(endCalendarRef.current));
            setIsEndCalendarOpen(!isEndCalendarOpen);
            setIsStartCalendarOpen(false);
          }}
        />

        {isEndCalendarOpen && (
          <div
            className={`absolute top-full z-[9999] mt-1 w-full min-w-[300px] ${
              endAlign === "right" ? "right-0" : "-left-[4px]"
            }`}
          >
            <div
              className={`rounded-lg shadow-lg border ${
                isDark
                  ? "bg-[#1e222d] border-[#3d3d3d] text-white"
                  : "bg-white border-gray-200 text-gray-900"
              }`}
            >
              <div className="p-3">
                <Calendar
                  onChange={handleEndDateSelect}
                  value={moment(endDate).tz("Asia/Kolkata").toDate()}
                  tileDisabled={tileDisabled}
                  tileClassName={(props) =>
                    tileClassName({ ...props, isStartCalendar: false })
                  }
                  minDate={startDate}
                  maxDate={maxDate}
                  locale="en-US"
                  calendarType="gregory"
                  showFixedNumberOfWeeks={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangePicker;
